import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../../lib/PropTypes';

import { useTheme, useThemeClassName } from '../../../contexts/ThemeContext';
import DrawDates from '../../partials/DrawDates';
import PrizeCard from '../../partials/PrizeCard';

import styles from '../../../../styles/items/draws-section-default.module.scss';

const propTypes = {
    title: PropTypes.node,
    description: PropTypes.node,
    draws: AppPropTypes.draw,
    className: PropTypes.string,
    datesClassName: PropTypes.string,
    prizeClassName: PropTypes.string,
    prizeCardClassName: PropTypes.string,
};

const defaultProps = {
    title: null,
    description: null,
    draws: null,
    className: null,
    datesClassName: null,
    prizeClassName: null,
    prizeCardClassName: null,
};

function DrawsSectionDefaultItem({
    title,
    description,
    draws: initialDraws,
    className,
    datesClassName,
    prizeClassName,
    prizeCardClassName,
}) {
    const { name: themeName = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const user = useUser();
    const { lastPincode } = user || {};
    const { groups = null } = lastPincode || {};

    const draws = useMemo(
        () =>
            (initialDraws || [])
                .filter(
                    ({ groups: drawGroups = null } = {}) =>
                        drawGroups === null ||
                        drawGroups.reduce((acc, group) => {
                            const found = (groups || []).find((id) => id === group) || null;
                            if (found) {
                                return true;
                            }
                            return acc;
                        }, false),
                )
                .filter((draw) => {
                    if (draw === null) {
                        return false;
                    }
                    const { groups: drawGroups = null } = draw || {};
                    if ((drawGroups || []).indexOf('normandin') !== -1) {
                        return draw.active;
                    }
                    return true;
                }),
        [initialDraws, groups, user],
    );

    const isYeOldePincode = (groups || []).indexOf('yeold') !== -1;

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.inner}>
                {title !== null ? <h4 className={styles.title}>{title}</h4> : null}
                {description !== null && draws.length > 1 ? (
                    <div className={styles.description}>{description}</div>
                ) : null}
                {draws.map((draw, drawIndex) => {
                    const {
                        id: drawId = null,
                        title: drawTitle,
                        description: drawDescription = null,
                        winnersShuffle: drawWinnersShuffle = true,
                        winnersCount: drawWinnersCount = null,
                        pointsByParticipation: drawPointsByParticipation = null,
                        prizes: drawPrizes = null,
                        batch = null,
                        groups: drawGroups = [],
                        image: drawImage = null,
                        winners: drawWinners = null,
                    } = draw || {};

                    const hasPointsEntry = drawPointsByParticipation !== null;
                    const hasTopWinners = hasPointsEntry && !drawWinnersShuffle;
                    const { prizes: batchPrizes = null } = batch || {};

                    const sortedCurrentDrawPrizes = useMemo(() => {
                        const hasBatchPrizes = batchPrizes !== null;
                        return hasBatchPrizes
                            ? sortBy(drawPrizes || [], ({ prize }) => {
                                  const { id: prizeId } = prize || {};
                                  const drawBatchPrize =
                                      batchPrizes.find(({ prize: batchPrize }) => {
                                          const { id: batchPrizeId } = batchPrize || {};
                                          return batchPrizeId === prizeId;
                                      }) || null;
                                  return drawBatchPrize !== null ? 0 : 1;
                              })
                            : drawPrizes || [];
                    }, [drawPrizes]);

                    const groupThemes = drawGroups.map(
                        (group) => styles[camelCase(`group-${group}`)],
                    );
                    const isYeOldeGroup = drawGroups.indexOf('yeold') !== -1;

                    const winnersString = drawWinners.length > 0 ? drawWinners.join(', ') : null;

                    return (
                        <div
                            className={classNames(styles.draw, `draw-${drawId}`, ...groupThemes)}
                            key={`draw-${drawId}`}
                        >
                            {hasTopWinners ? (
                                <div className={styles.topWinners}>
                                    {drawWinnersCount === 1 ? (
                                        <FormattedMessage
                                            defaultMessage="The participant who finishes in 1st place will win the prize."
                                            description="Draws page description"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="A prize awaits the top {count} participants."
                                            description="Draws page description"
                                            values={{
                                                count: drawWinnersCount,
                                            }}
                                        />
                                    )}
                                </div>
                            ) : null}
                            {(isYeOldeGroup ||
                                themeName === 'rhoneacademy' ||
                                themeName === 'perfect') &&
                            drawImage !== null ? (
                                <img className={styles.image} src={drawImage.url} alt={drawTitle} />
                            ) : null}
                            {(drawTitle !== null && drawTitle !== title && draws.length === 1) ||
                            (drawGroups !== null && drawGroups.length > 0) ||
                            drawIndex > 0 ? (
                                <h4 className={styles.title}>{drawTitle}</h4>
                            ) : null}
                            {drawDescription !== null ? (
                                <div className={styles.description}>{drawDescription}</div>
                            ) : null}
                            {isYeOldePincode && !isYeOldeGroup ? (
                                <h4 className={styles.title}>
                                    <FormattedMessage
                                        defaultMessage="{count, plural, one {Regular prize} other {Regular prizes}}"
                                        description="NFL - Ye Olde"
                                        values={{ count: sortedCurrentDrawPrizes.length }}
                                    />
                                </h4>
                            ) : null}
                            {description !== null && draws.length === 1 ? (
                                <div className={styles.description}>{description}</div>
                            ) : null}
                            <DrawDates
                                draw={draw}
                                className={classNames([
                                    styles.dates,
                                    {
                                        [styles.multiple]: draws.length > 1,
                                        [datesClassName]: datesClassName !== null,
                                    },
                                ])}
                            />
                            <div className={styles.prizes}>
                                {sortedCurrentDrawPrizes.map((drawPrize, prizeIndex) => {
                                    const { prize = null, quantity = null } = drawPrize;

                                    const {
                                        id: prizeId,
                                        name: prizeName = null,
                                        legal: prizeLegal = null,
                                        image: prizeImage = null,
                                        badge: prizeBadge = null,
                                        description: prizeDescription = null,
                                        note: prizeNote = null,
                                        winnable = true,
                                    } = prize || {};

                                    const hasBatchPrizes = batchPrizes !== null;
                                    const drawBatchPrize = hasBatchPrizes
                                        ? batchPrizes.find(({ prize: batchPrize }) => {
                                              const { id: batchPrizeId } = batchPrize || {};
                                              return batchPrizeId === prizeId;
                                          }) || null
                                        : null;
                                    const { quantity: batchQuantity = null } = drawBatchPrize || {};
                                    const finalDescription =
                                        prizeDescription !== null ? prizeDescription : prizeLegal;

                                    // TODO: fix this later
                                    const notAvailable = false; // hasBatchPrizes && drawBatchPrize === null;

                                    return (
                                        <div
                                            key={`prize-${prizeId}`}
                                            className={classNames([
                                                styles.prize,
                                                {
                                                    [styles.notAvailable]: notAvailable,
                                                },
                                                prizeClassName,
                                            ])}
                                        >
                                            <PrizeCard
                                                key={`prize-${prizeIndex + 1}`}
                                                className={classNames([
                                                    styles.prizeCard,
                                                    prizeCardClassName,
                                                ])}
                                                name={prizeName}
                                                description={finalDescription}
                                                quantity={
                                                    hasBatchPrizes && themeName !== 'nfl'
                                                        ? batchQuantity
                                                        : quantity
                                                }
                                                image={prizeImage}
                                                badge={prizeBadge}
                                                winnable={winnable}
                                                note={prizeNote}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {themeName === 'rhoneacademy' ? (
                                <div className={styles.winners}>
                                    <FormattedMessage
                                        defaultMessage="Winners: {name, select, null {to be determined} other {{name}}}"
                                        description="Draw Winner"
                                        values={{ name: winnersString }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

DrawsSectionDefaultItem.propTypes = propTypes;
DrawsSectionDefaultItem.defaultProps = defaultProps;

export default DrawsSectionDefaultItem;
